import { DetailsList, DetailsListLayoutMode, IColumn, IContextualMenuItem, Selection, SelectionMode } from "@fluentui/react";
import { RootState } from "app/store";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import CustomPanel from "components/controls/CustomPanel";
import { SimpleGridStyles } from "components/layout/StickyDetailList";
import { suggestNewCESNumber } from "global/Application";
import { useApplication } from "global/Application/Context";
import {
    ICESNumber as FAM_ICESNumber
} from "model/FlexibleAttributeModel";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import GroupStructureCESEditor from "../GroupStructureCESEditor";
import { IIGroupStructureCESViewProps } from "./IGroupStructureCESViewProps.type";

interface ISelect {
    index?: number,
    data: FAM_ICESNumber,
    type: 'add' | 'edit'
}

type TGridItem = IGridItem<FAM_ICESNumber>

const GroupStructureCESView: React.FunctionComponent<IIGroupStructureCESViewProps> = (props) => {

    const application = useApplication();
    const state = useSelector((state: RootState) => state);
    const [isOpenEditor, setIsOpenEditor] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const columns = useMemo(() => {
        const columns: IColumn[] = [
            { key: 'cesNumber', name: 'CES Client Number', fieldName: 'cesNumber', minWidth: 120, maxWidth: 140, isResizable: true },
        ]
        return columns;
    }, []);

    const items = useMemo(() => {

        const items: TGridItem[] = [];

        state.data.json.groupStructure.cesNumbers.forEach((eachCESNumber, index) => {
            const rec = {
                _index: index,
                key: eachCESNumber.id,
                cesNumber: eachCESNumber.cesNumber,
                data: eachCESNumber
            }

            // skip deactivated records/only show deactivated records if permitted at group config properties level (xnor)
            if (eachCESNumber.deactivated !== state.data.json.showInActives?.value)
                return;

            items.push(rec);
        });

        return items;
    }, [
        state.data.json.groupStructure.cesNumbers,
        state.data.json.showInActives?.value
    ]);

    const selection = useMemo(() => {
        return new Selection({
            onSelectionChanged: () => {

                if (selection.getSelectedCount() > 0)
                    setCanEdit(true)
                else
                    setCanEdit(false)
            },
            selectionMode: SelectionMode.single,
        })
    }, []);

    const [selectedItem, setSelectedItem] = useState<ISelect | null>(null);

    const menu = useMemo(() => {
        const menu: IContextualMenuItem[] = [];

        if (!props.disabled && application.ChangeTypePermissions.groupStructure.ces.add)
            menu.push(
                {
                    key: 'groupConfiguration.groupStructure.ces.add',
                    name: 'Add',
                    icon: 'DocumentManagement',
                    onClick: (event, item) => {
                        if (event?.stopPropagation)
                            event.stopPropagation();

                        setSelectedItem({
                            data: suggestNewCESNumber(state.data.json.groupStructure.cesNumbers),
                            type: 'add',
                        });

                        setIsOpenEditor(true);
                    },
                },
            );

        if (!props.disabled)
            if (canEdit)
                menu.push({
                    key: `groupNumberPageEdit`,
                    name: 'Edit',
                    icon: 'PageEdit',
                    onClick: (event, item) => {
                        if (event?.stopPropagation)
                            event.stopPropagation();

                        const selectedItems = selection.getSelection() as TGridItem[];
                        if (selectedItems.length > 0) {
                            setSelectedItem({
                                index: selectedItems[0]._index,
                                data: selectedItems[0].data!,
                                type: 'edit',
                            });

                            setIsOpenEditor(true);
                        }
                    },
                });

        return menu;
    }, [canEdit, selection, state.data.json.groupStructure?.cesNumbers, props.disabled]);


    return <div>
        <CustomPanel
            title={`CES Client Numbers (${items.length})`}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"groupConfiguration.groupStructure.cesNumbers.view"}
            items={menu}
        >
            <div style={{ minHeight: 15 }}></div>
            <DetailsList
                items={items}
                columns={columns}
                styles={SimpleGridStyles}
                getKey={(item, index) => { return item.key }}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                compact={true}
                selection={selection}
                selectionMode={SelectionMode.single}
                onItemInvoked={(item: TGridItem) => {
                    setSelectedItem({
                        index: item.index,
                        data: item.data!,
                        type: 'edit',
                    });
                    setIsOpenEditor(true);
                }}
            />
            <div style={{ minHeight: 15 }}></div>
        </CustomPanel>

        <GroupStructureCESEditor
            disabled={props.disabled}
            isOpen={isOpenEditor}
            value={selectedItem?.data}
            isAdd={selectedItem?.type === 'add'}
            onChange={(event, value) => setSelectedItem(null)}
            onCancel={() => setIsOpenEditor(false)}
            onDismiss={() => setIsOpenEditor(false)}
        />
    </div>
}

export default GroupStructureCESView